import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import {
  FaHome,
  FaUsers,
  FaBoxes,
  FaFileInvoiceDollar,
  FaWallet,
  FaChartBar,
  FaRegFileAlt,
  FaCog,
  FaBell,
  FaSignOutAlt,
  FaBars,
  FaTimes,
} from "react-icons/fa";

import { signOut } from "firebase/auth";
import { auth } from "../../firebase/firebase";

import "./Topbar.css";

const NAV_ITEMS = [
  {
    to: "/dashboard",
    label: "Dashboard",
    icon: FaHome,
    end: true,
  },
  {
    to: "/dashboard/clients",
    label: "Clients",
    icon: FaUsers,
  },
  {
    to: "/dashboard/estimation",
    label: "Estimation",
    icon: FaChartBar,
  },
  {
    to: "/dashboard/invoice",
    label: "Invoice",
    icon: FaFileInvoiceDollar,
  },
  // New — Payments now owns Final Quotation + Payment History/Timeline,
  // split out of the old Materials page. Invoice.js is still the only
  // place a payment is actually recorded.
  {
    to: "/dashboard/payments",
    label: "Payments",
    icon: FaWallet,
  },
  {
    to: "/dashboard/materials",
    label: "Materials",
    icon: FaBoxes,
  },
   {
    to: "/dashboard/notes",
    label: "Notes",
    icon: FaRegFileAlt,
  },
  {
    to: "/dashboard/settings",
    label: "Settings",
    icon: FaCog,
  },
];

function Topbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const logout = async () => {
    await signOut(auth);
    navigate("/Admin");
  };

  return (
    <header className="topbar">
      {/* row 1: logo + profile/logout */}
      <div className="topbar-row1">
        <div className="logo-area">
          <img src="/logo/company logo.jpeg" alt="Sarrathi logo" />
          <div className="logo-text">
            <h2>Sarrathi</h2>
            <span>CONSTRUCTIONS ERP</span>
          </div>
        </div>

        <div className="top-right">
          <button className="icon-btn" aria-label="Notifications">
            <FaBell />
            <span className="notif-dot" />
          </button>

          <div className="admin-chip">
            <div className="admin-avatar">A</div>
            <div className="admin-meta">
              <strong>Administrator</strong>
              <span>Online</span>
            </div>
          </div>

          <button className="icon-btn logout" onClick={logout} aria-label="Logout">
            <FaSignOutAlt />
            <span className="logout-label">Logout</span>
          </button>

          <button
            className="icon-btn menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* row 2: nav links on their own row, centered, free to wrap if ever needed */}
      <nav className="topbar-row2">
        {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className="nav-link">
            {({ isActive }) => (
              <span className="nav-link-inner">
                {isActive && (
                  <motion.span
                    layoutId="active-pill"
                    className="active-pill"
                    transition={{ type: "spring", stiffness: 420, damping: 34 }}
                  />
                )}
                <Icon className="nav-icon" />
                <span className="nav-label">{label}</span>
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* mobile dropdown — normal flow, pushes content down, never overlays */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
          >
            {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `mobile-link ${isActive ? "mobile-link--active" : ""}`
                }
                onClick={() => setMenuOpen(false)}
              >
                <Icon />
                <span>{label}</span>
              </NavLink>
            ))}
            <button
              className="mobile-logout"
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
            >
              <FaSignOutAlt />
              Logout
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Topbar;